import { useSelectedEducationLevelType } from '@/app/Providers/EducationLevelsContext/educationLevelState'
import {
  DocumentBaseType,
  EducationLevelType,
} from '@/graphql/generated/graphql'
import { AuthUser, useAuthUser } from '@/hooks/useAuth/zustand'
import { checkIsDirectCertificatePermission } from './permission.utils'

type PermissionElement = {
  code: string
  title: string
  description: string
}

const administrativePermissions = [
  {
    code: 'MANAGE_REUSABLE_IES',
    title: 'Gerenciar IES',
    description: 'Permite cadastrar/editar/excluir dados da instituição.',
  },
  {
    code: 'MANAGE_REUSABLE_ANY',
    title: 'Gerenciar dados acadêmicos reusáveis',
    description:
      'Permite cadastrar/editar/excluir dados acadêmicos que podem ser reutilizados para qualquer documento. Ex: Cursos, Docentes e Disciplinas.',
  },
  {
    code: 'MANAGE_USERS',
    title: 'Gerenciar usuários',
    description:
      'Permite cadastrar/editar/excluir usuários e gerenciar permissões.',
  },
  {
    code: 'MANAGE_API_KEYS_OWN',
    title: 'Gerenciar chaves de API',
    description:
      'Permite cadastrar/excluir chaves de API para seu próprio usuário.',
  },
  {
    code: 'MANAGE_FINANCES',
    title: 'Gerenciar planos e créditos',
    description:
      'Permite recarregar créditos, alterar ou cancelar planos, visualizar histórico de pagamentos, visualizar uso de créditos.',
  },
] as const satisfies PermissionElement[]

export const documentAcademicDocumentation = [
  {
    code: 'CREATE_ACADEMIC_DOCUMENTATION',
    title: 'Criar Documentação Acadêmica',
    description: 'Permite criar Documentações Acadêmicas.',
  },
  {
    code: 'UPDATE_ACADEMIC_DOCUMENTATION_OWN',
    title: 'Editar Documentação Acadêmica (do próprio usuário)',
    description:
      'Permite que o usuário edite Documentações Acadêmicas já criadas pelo seu próprio usuário.',
  },
  {
    code: 'UPDATE_ACADEMIC_DOCUMENTATION_ANY',
    title: 'Editar Documentação Acadêmica (de todos os usuários da IES)',
    description:
      'Permite que o usuário edite Documentações Acadêmicas já criadas por qualquer usuário da mesma IES.',
  },
  {
    code: 'REVIEW_ACADEMIC_DOCUMENTATION',
    title: 'Revisar uma Documentação Acadêmica',
    description:
      'Permite revisar Documentações Acadêmicas com status Pendente de revisão.',
  },
  {
    code: 'DELETE_ACADEMIC_DOCUMENTATION_OWN',
    title: 'Deletar Documentação Acadêmica (do próprio usuário)',
    description:
      'Permite que o usuário delete Documentações Acadêmicas já criadas pelo seu próprio usuário.',
  },
  {
    code: 'DELETE_ACADEMIC_DOCUMENTATION_ANY',
    title: 'Deletar Documentação Acadêmica (de todos os usuários da IES)',
    description:
      'Permite que o usuário delete documentos já criados por qualquer usuário da mesma IES.',
  },
  {
    code: 'INVITE_SIGNERS_ACADEMIC_DOCUMENTATION',
    title: 'Convidar assinantes para Documentação Acadêmica',
    description:
      'Permite convidar usuários para realizarem assinaturas na Documentação Acadêmica.',
  },
  {
    code: 'SIGN_ACADEMIC_DOCUMENTATION_ECPF',
    title: 'Assinar Documentação Acadêmica como pessoa física',
    description:
      'Permite assinar Documentações Acadêmicas em assinaturas de pessoa física.',
  },
  {
    code: 'SIGN_ACADEMIC_DOCUMENTATION_ECNPJ',
    title: 'Assinar Documentação Acadêmica pela instituição',
    description:
      'Permite assinar Documentações Acadêmicas em assinaturas de pessoa jurídica.',
  },
  {
    code: 'ACADEMIC_DOCUMENTATION_BULK_DOWNLOAD',
    title: 'Download em lote de Documentação Acadêmica',
    description:
      'Permite que o usuário baixe múltiplas Documentações Acadêmicas.',
  },
  {
    code: 'REVOKE_ACADEMIC_DOCUMENTATION',
    title: 'Revogar Documentação Acadêmica',
    description: 'Permite que o usuário revogue uma Documentação Acadêmica.',
  },
] as const satisfies PermissionElement[]

const documentFinalAcademicTranscript = [
  {
    code: 'CREATE_FINAL_ACADEMIC_TRANSCRIPT',
    title: 'Criar Histórico Final',
    description: 'Permite criar Históricos Finais.',
  },
  {
    code: 'DELETE_FINAL_ACADEMIC_TRANSCRIPT_OWN',
    title: 'Deletar Histórico Final (do próprio usuário)',
    description:
      'Permite que o usuário delete Históricos já criados pelo seu próprio usuário.',
  },
  {
    code: 'DELETE_FINAL_ACADEMIC_TRANSCRIPT_ANY',
    title: 'Deletar Histórico Final (de todos os usuários da IES)',
    description:
      'Permite que o usuário delete Históricos já criados por qualquer usuário da mesma IES.',
  },
  {
    code: 'INVITE_SIGNERS_FINAL_ACADEMIC_TRANSCRIPT',
    title: 'Convidar assinantes para Histórico Final',
    description:
      'Permite convidar usuários para realizarem assinaturas no Histórico Final.',
  },
  {
    code: 'SIGN_FINAL_ACADEMIC_TRANSCRIPT_ECPF',
    title: 'Assinar Histórico Final como pessoa física',
    description:
      'Permite assinar Históricos Finais em assinaturas de pessoa física.',
  },
  {
    code: 'SIGN_FINAL_ACADEMIC_TRANSCRIPT_ECNPJ',
    title: 'Assinar Histórico Final pela instituição',
    description:
      'Permite assinar Históricos Finais em assinaturas de pessoa jurídica.',
  },
  {
    code: 'FINAL_ACADEMIC_TRANSCRIPT_BULK_DOWNLOAD',
    title: 'Download em lote de Histórico Final',
    description: 'Permite que o usuário baixe múltiplos Históricos Finais.',
  },
  {
    code: 'REVOKE_FINAL_ACADEMIC_TRANSCRIPT',
    title: 'Revogar Histórico Final',
    description: 'Permite que o usuário revogue um Histórico Final.',
  },
] as const satisfies PermissionElement[]

const documentDegree = [
  {
    code: 'DEGREE_IMPORT',
    title: 'Diploma Digital',
    description:
      'Permite que o usuário importe o XML do diploma digital, como também o arquivo da representação visual.',
  },
  {
    code: 'UPDATE_DEGREE_OWN',
    title: 'Editar um Diploma (do próprio usuário)',
    description:
      'Permite que o usuário edite um Diploma já importados pelo seu próprio usuário.',
  },
  {
    code: 'UPDATE_DEGREE_ANY',
    title: 'Editar Diploma (de todos os usuários da IES)',
    description:
      'Permite que o usuário edite Diplomas já importados por qualquer usuário da mesma IES.',
  },
  {
    code: 'DELETE_DEGREE_OWN',
    title: 'Deletar Diploma (do próprio usuário)',
    description:
      'Permite que o usuário delete um Diploma já importado pelo seu próprio usuário.',
  },
  {
    code: 'DELETE_DEGREE_ANY',
    title: 'Deletar Diplomas (de todos os usuários da IES)',
    description:
      'Permite que o usuário delete Diplomas já importados por qualquer usuário da mesma IES.',
  },
  {
    code: 'REVOKE_DEGREE',
    title: 'Revogar Diploma',
    description: 'Permite que o usuário revogue um Diploma.',
  },
] as const satisfies PermissionElement[]

const documentInspectionFile = [
  {
    code: 'CREATE_AUDIT_FILE_ISSUER',
    title: 'Criar Arquivo de Fiscalização',
    description: 'Permite criar Arquivos de Fiscalização.',
  },
  {
    code: 'UPDATE_AUDIT_FILE_ISSUER_OWN',
    title: 'Editar Arquivo de Fiscalização (do próprio usuário)',
    description:
      'Permite que o usuário edite Arquivos de Fiscalização já criados pelo seu próprio usuário.',
  },
  {
    code: 'UPDATE_AUDIT_FILE_ISSUER_ANY',
    title: 'Editar Arquivo de Fiscalização (de todos os usuários da IES)',
    description:
      'Permite que o usuário edite Arquivos de Fiscalização já criados por qualquer usuário da mesma IES.',
  },
  {
    code: 'DELETE_AUDIT_FILE_ISSUER_OWN',
    title: 'Deletar Arquivo de Fiscalização (do próprio usuário)',
    description:
      'Permite que o usuário delete Arquivos de Fiscalização já criados pelo seu próprio usuário.',
  },
  {
    code: 'DELETE_AUDIT_FILE_ISSUER_ANY',
    title: 'Deletar Arquivo de Fiscalização (de todos os usuários da IES)',
    description:
      'Permite que o usuário delete Arquivos de Fiscalização já criados por qualquer usuário da mesma IES.',
  },
  // {
  //   code: 'INVITE_SIGNERS_AUDIT_FILE_ISSUER',
  //   title: 'Convidar assinantes para Arquivo de Fiscalização',
  //   description:
  //     'Permite convidar usuários para realizarem assinaturas no Arquivo de Fiscalização.',
  // },
  // {
  //   code: 'SIGN_AUDIT_FILE_ISSUER_ECPF',
  //   title: 'Assinar Arquivo de Fiscalização como pessoa física',
  //   description:
  //     'Permite assinar Arquivos de Fiscalização em assinaturas de pessoa física.',
  // },
  {
    code: 'SIGN_AUDIT_FILE_ISSUER_ECNPJ',
    title: 'Assinar Arquivo de Fiscalização pela instituição',
    description:
      'Permite assinar Arquivos de Fiscalização em assinaturas de pessoa jurídica.',
  },
  {
    code: 'AUDIT_FILE_ISSUER_BULK_DOWNLOAD',
    title: 'Download em lote de Arquivo de Fiscalização',
    description:
      'Permite que o usuário baixe múltiplos Arquivos de Fiscalização.',
  },
  {
    code: 'REVOKE_AUDIT_FILE_ISSUER',
    title: 'Revogar Arquivo de Fiscalização',
    description: 'Permite que o usuário revogue uma Arquivo de Fiscalização.',
  },
  {
    code: 'REVIEW_AUDIT_FILE_ISSUER',
    title: 'Revisar um arquivo de fiscalização',
    description:
      'Permite revisar um arquivo de fiscalização com status Pendente de revisão.',
  },
] as const satisfies PermissionElement[]

export const documentSchoolCurriculum = [
  {
    code: 'CREATE_SCHOOL_CURRICULUM',
    title: 'Criar Currículo Escolar',
    description: 'Permite criar Currículos Escolares.',
  },
  {
    code: 'UPDATE_SCHOOL_CURRICULUM_OWN',
    title: 'Editar Currículo Escolar (do próprio usuário)',
    description:
      'Permite que o usuário edite Currículos Escolares já criados pelo seu próprio usuário.',
  },
  {
    code: 'UPDATE_SCHOOL_CURRICULUM_ANY',
    title: 'Editar Currículo Escolar (de todos os usuários da IES)',
    description:
      'Permite que o usuário edite Currículos Escolares já criados por qualquer usuário da mesma IES.',
  },
  {
    code: 'REVIEW_SCHOOL_CURRICULUM',
    title: 'Revisar um Currículo Escolar',
    description:
      'Permite revisar Currículos Escolares com status Pendente de revisão.',
  },
  {
    code: 'DELETE_SCHOOL_CURRICULUM_OWN',
    title: 'Deletar Currículo Escolar (do próprio usuário)',
    description:
      'Permite que o usuário delete Currículos Escolares já criados pelo seu próprio usuário.',
  },
  {
    code: 'DELETE_SCHOOL_CURRICULUM_ANY',
    title: 'Deletar Currículo Escolar (de todos os usuários da IES)',
    description:
      'Permite que o usuário delete documentos já criados por qualquer usuário da mesma IES.',
  },
  {
    code: 'SIGN_SCHOOL_CURRICULUM_ECNPJ',
    title: 'Assinar Currículo Escolar pela instituição',
    description:
      'Permite assinar Currículos Escolares em assinaturas de pessoa jurídica.',
  },
  {
    code: 'SCHOOL_CURRICULUM_BULK_DOWNLOAD',
    title: 'Download em lote de Currículo Escolar',
    description: 'Permite que o usuário baixe múltiplos Currículos Escolares.',
  },
  {
    code: 'REVOKE_SCHOOL_CURRICULUM',
    title: 'Revogar Currículo Escolar',
    description: 'Permite que o usuário revogue um Currículo Escolar.',
  },
] as const satisfies PermissionElement[]

export const documentCertificate = [
  {
    code: 'MANAGE_CERTIFICATE_REUSABLE_CURRICULUM',
    title: 'Gerenciar grade curricular',
    description: 'Permite cadastrar/editar/excluir dados da grade curricular.',
  },
  {
    code: 'MANAGE_CERTIFICATE_REUSABLE_ANY',
    title: 'Gerenciar dados reusáveis da grade curricular',
    description:
      'Permite cadastrar/editar/excluir dados reusáveis que podem ser reutilizados para grade curricular. Ex: Cursos, Docentes e Disciplinas.',
  },
  {
    code: 'CREATE_CERTIFICATE',
    title: 'Criar certificado de pós-graduação',
    description:
      'Permite a criação de certificado de pós-graduação para um aluno.',
  },
  {
    code: 'CREATE_CERTIFICATE_TEMPLATE',
    title: 'Criar modelo de certificado de pós-graduação',
    description: 'Permite criar modelos de certificados de pós-graduação',
  },
  {
    code: 'UPDATE_CERTIFICATE_TEMPLATE_OWN',
    title: 'Editar modelo de certificado de pós-graduação (do próprio usuário)',
    description:
      'Permite que o usuário edite modelos de certificados de pós-graduação criados pelo seu próprio usuário.',
  },
  {
    code: 'UPDATE_CERTIFICATE_TEMPLATE_ANY',
    title:
      'Editar modelo de certificado de pós-graduação (de todos os usuários da IES)',
    description:
      'Permite que o usuário edite modelos de certificados de pós-graduação criados por qualquer usuário da mesma IES.',
  },
  {
    code: 'DELETE_CERTIFICATE_TEMPLATE_OWN',
    title:
      'Deletar modelo de certificado de pós-graduação (do próprio usuário)',
    description:
      'Permite que o usuário delete modelos de certificados de pós-graduação criados pelo seu próprio usuário.',
  },
  {
    code: 'DELETE_CERTIFICATE_TEMPLATE_ANY',
    title:
      'Deletar modelo de certificado de pós-graduação (de todos os usuários da IES)',
    description:
      'Permite que o usuário delete modelos de certificados de pós-graduação criados por qualquer usuário da mesma IES.',
  },
  {
    code: 'DELETE_CERTIFICATE_OWN',
    title: 'Deletar certificado de pós-graduação (do próprio usuário)',
    description:
      'Permite que o usuário delete certificados de pós-graduação já criados pelo seu próprio usuário.',
  },
  {
    code: 'SIGN_CERTIFICATE',
    title: 'Assinar certificado de pós-graduação',
    description:
      'Permite assinar certificados de pós-graduação em assinaturas de pessoa física.',
  },
  {
    code: 'DELETE_CERTIFICATE_ANY',
    title: 'Deletar certificado de pós-graduação (de todos os usuários da IES)',
    description:
      'Permite que o usuário delete certificados de pós-graduação já criados por qualquer usuário da mesma IES.',
  },
  {
    code: 'REVOKE_CERTIFICATE',
    title: 'Revogar certificado de pós-graduação',
    description:
      'Permite que o usuário revogue um certificado de pós-graduação.',
  },
  {
    code: 'MANAGE_CERTIFICATE_STUDENT',
    title: 'Gerenciar alunos.',
    description:
      'Permite cadastrar/editar/excluir informações de alunos ou participantes.',
  },
] as const satisfies PermissionElement[]

const replaceIES = (str: string) => str.replace('IES', 'conta')
const replacePostGraduate = (str: string) =>
  str.replace(/ ?(de pós-graduação)/g, '')
const replaceCertificateGeneric = (str: string) =>
  replaceIES(replacePostGraduate(str))
const documentCertificateGeneric = documentCertificate.map((el) => ({
  code: `${el.code}_GENERIC` as const,
  description: replaceCertificateGeneric(el.description),
  title: replaceCertificateGeneric(el.title),
})) satisfies PermissionElement[]

const replacePostGraduateCertificate = (str: string) =>
  str.replace(
    /(certificados? de pós-graduação)/g,
    'representação visual de diploma digital',
  )
const replaceRvdd = (str: string) => replacePostGraduateCertificate(str)
const documentCertificateRvdd = documentCertificate.map((el) => ({
  code: `${el.code}_RVDD` as const,
  description: replaceRvdd(el.description),
  title: replaceRvdd(el.title),
})) satisfies PermissionElement[]

export const permissionsBase = [
  ...administrativePermissions,
  ...documentAcademicDocumentation,
  ...documentFinalAcademicTranscript,
  ...documentDegree,
  ...documentInspectionFile,
  ...documentCertificate,
  ...documentSchoolCurriculum,
] as const

export const permissions = [
  ...permissionsBase,
  ...documentCertificateGeneric,
  ...documentCertificateRvdd,
] as const

/**
 * "Base" means all permissions without the "_GENERIC" suffix
 */
export type PermissionBaseName = (typeof permissionsBase)[number]['code']
/**
 * "Pure" means all permissions with and without the "_CERTIFICATE" suffix
 */
export type PermissionPureName = (typeof permissions)[number]['code']

const userHasPermissionPure = (
  userPermissions: PermissionPureName[] | undefined,
  neededPermission: PermissionPureName,
): boolean => {
  return Boolean(userPermissions?.includes(neededPermission))
}

/**
 * Can convert permission name based on selected education level
 */
export const userHasPermissionFn = ({
  neededPermission,
  selectedLevel,
  userPermissions,
  pure,
}: {
  userPermissions: PermissionBaseName[] | undefined
  neededPermission: PermissionBaseName
  selectedLevel: EducationLevelType
  pure?: boolean
}) => {
  if (pure) return userHasPermissionPure(userPermissions, neededPermission)

  const isCertificateDirectPermission =
    checkIsDirectCertificatePermission(neededPermission)

  const certificateEndings = {
    POST_GRADUATE: '', // unreachable once hasPure is triggered before
    GENERIC_CERTIFICATES: '_GENERIC',
    UNDER_GRADUATE: '_RVDD',
  } as const satisfies Record<EducationLevelType, string>

  if (isCertificateDirectPermission) {
    return userHasPermissionPure(
      userPermissions,
      `${neededPermission}${certificateEndings[selectedLevel]}`,
    )
  }

  return userHasPermissionPure(userPermissions, neededPermission)
}

type DocumentBaseTypeStr = `${DocumentBaseType}`
export const hasUserPermissionOnDocType = (
  category: DocumentBaseTypeStr,
  userPermissions: PermissionBaseName[] | undefined,
): boolean => {
  return Boolean(
    userPermissions?.some((permission) => permission.includes(category)),
  )
}
